import * as React from "react"
import {
  type CategoryType,
  DirectionFromOrigin,
  useSummarizeTransactionsByTimeByCategoryQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import {
  type TopActivity,
  TopActivityContainer,
  useTopActivitySortMode,
} from "src/frontend/components/OS/Shared/Widgets/TopActivities/shared"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import { ComponentHeaderTitle } from "src/frontend/components/Shared/Layout/Components/Headers/ComponentSummary"
import { TopCategoriesList } from "src/frontend/components/Shared/Layout/Components/Lists/TopCategoriesList"
import { ComponentSize } from "src/frontend/components/Shared/Layout/ComponentSize"
import type { EntityCategoryTransactionSummary } from "src/frontend/types"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

/*
  INTERFACES
*/

interface TopCategoriesProps {
  highlight: TopActivity
  categoryTypes: CategoryType[]
}

/*
  COMPONENTS
*/

export const TopCategories: React.FC<TopCategoriesProps> = ({ highlight, categoryTypes }) => {
  const origin = useIntervalOrigin()
  const { categoriesSummaries, loading } = useCategoriesSummariesQuery(highlight, categoryTypes)
  const periodName = dateTimeHelper.displayNameForIntervalOriginRange(origin, { delimiter: " - " })

  return (
    <TopActivityContainer>
      <ComponentHeaderTitle
        title="Top Categories"
        componentSize={ComponentSize.Small}
        periodName={periodName}
      />
      <TopCategoriesList summaries={categoriesSummaries} loading={loading} />
    </TopActivityContainer>
  )
}

/*
  HOOKS
*/
function useCategoriesSummariesQuery(highlight: TopActivity, categoryTypes: CategoryType[]) {
  const viewKey = useViewVersion()
  const intervalOrigin = useIntervalOrigin()
  const sort = useTopActivitySortMode(highlight, categoryTypes)

  const { data, loading } = useSummarizeTransactionsByTimeByCategoryQuery({
    variables: {
      direction: DirectionFromOrigin.Past,
      filter: {
        viewKey,
        categoryTypes,
        withoutCategoryAncestors: true,
      },
      origin: {
        ...intervalOrigin,
        intervalCount: 1,
      },
      sort,
      limit: 5,
    },
  })

  return React.useMemo(() => {
    const categoriesSummaries =
      data?.dimensionalTransactionSummary.time[0]?.by.category.map<EntityCategoryTransactionSummary>(
        (c) => ({
          category: c.categoryObject,
          summary: c.summary,
        })
      )
    return { categoriesSummaries, loading }
  }, [data, loading])
}
